import type { PasteUpdate, ParsedTable } from "./types";

/**
 * Parse a human-formatted numeric string into a number.
 * Supports thousands separators, percentages, currency symbols,
 * various minus signs, and accounting negatives in parentheses.
 * Returns null if not parseable as a finite number.
 */
function parseFormattedNumber(rawValue: string): number | null {
  if (!rawValue) return null;
  let text = rawValue.trim();

  // Handle accounting negatives like (1,234.56)
  let isNegative = false;
  if (text.startsWith("(") && text.endsWith(")")) {
    isNegative = true;
    text = text.slice(1, -1).trim();
  }

  text = text.replace(/[−–—]/g, "-"); // normalize hyphens
  text = text.replace(/[+$€£¥₹]/g, ""); // remove currency symbols
  text = text.replace(/%/g, ""); // remove percent sign
  // Remove seperators (commas, spaces, thin spaces)
  text = text.replace(/[\s,\u00A0\u2009\u202F]/g, "");

  // If what's left is just a minus, it's not a number
  if (text === "-" || text === "") return null;

  const num = Number(text);
  if (!Number.isFinite(num)) return null;
  return isNegative ? -num : num;
}

/**
 * Detect if a row looks like headers by checking if most cells are non-numeric text
 */
function looksLikeHeaderRow(row: string[]): boolean {
  if (row.length === 0) return false;

  let textCells = 0;
  let totalCells = 0;

  for (const cell of row) {
    if (cell.trim() === "") continue; // Skip empty cells
    totalCells++;

    // If it's not a number, consider it text
    if (parseFormattedNumber(cell) === null) {
      textCells++;
    }
  }

  return totalCells === 0 || totalCells - textCells <= 1; // allow one non-numeric cell
}

export function parseClipboardToTable(text: string): ParsedTable {
  const lines = text
    .split("\n")
    .map((l) => l.replace(/\r$/, ""))
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const rows = lines.map((line) => line.split("\t").map((c) => c.trim()));
  const numColumns = rows.reduce((max, r) => Math.max(max, r.length), 0);

  const hasHeaderRow = rows.length > 1 && looksLikeHeaderRow(rows[0] ?? []);
  const headers = hasHeaderRow ? rows[0] : undefined;

  return {
    rows,
    numColumns,
    headers,
    hasHeaderRow,
  };
}

export function detectNumericColumns(table: ParsedTable): number[] {
  const candidates: number[] = [];
  const dataStartRow = table.hasHeaderRow ? 1 : 0;
  const dataRows = table.rows.slice(dataStartRow);

  for (let col = 1; col < table.numColumns; col++) {
    let allNumeric = true;
    for (const row of dataRows) {
      if (row.length <= col) {
        continue;
      }
      const value = row[col];
      if (value === "" || value === undefined) continue;
      const num = parseFormattedNumber(value);
      if (num === null) {
        allNumeric = false;
        break;
      }
    }
    if (allNumeric) candidates.push(col);
  }
  return candidates;
}

export function buildUpdatesFromTable(
  table: ParsedTable,
  quantityColIndex: number | null
): { valid: boolean; updates: PasteUpdate[] } {
  if (table.rows.length === 0) return { valid: false, updates: [] };
  if (table.numColumns < 2) return { valid: false, updates: [] };

  const dataStartRow = table.hasHeaderRow ? 1 : 0;
  const dataRows = table.rows.slice(dataStartRow);

  const useCol = quantityColIndex ?? 1;
  const updates: PasteUpdate[] = [];
  for (const row of dataRows) {
    const name = (row[0] ?? "").trim();
    const qtyRaw = (row[useCol] ?? "").trim();
    if (!name) continue;
    if (qtyRaw === "") continue;
    const quantity = parseFormattedNumber(qtyRaw);
    if (quantity === null) continue;
    updates.push({ material_name: name, quantity });
  }
  return { valid: updates.length > 0, updates };
}
